import { useId } from 'react';

import ActionButton from '@/components/Button/ActionButton';
import Icon from '@/components/Icon/Icon';
import Input from '@/components/Input/Input';
import { HStack, VStack } from '@/components/Layout';
import { SelectField } from '@/components/Select/SelectField';
import { Text } from '@/components/Typography';
import type { SnsAccountListItem } from '@/features/section-attribute/types';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

type SnsType = NonNullable<SnsAccountListItem['snsType']>;

type Props = CFormItemProps & {
  value?: SnsAccountListItem[];
  onChange: (value: SnsAccountListItem[]) => void;
};

function SnsAccountCard({
  item,
  getSnsTypeLabel,
}: {
  item: SnsAccountListItem;
  getSnsTypeLabel: (snsType: SnsType) => string;
}) {
  const typeLabel = item.snsType ? getSnsTypeLabel(item.snsType) : '';

  return (
    <div className="flex flex-col gap-2">
      <Text variant="labelSemiLarge">{typeLabel}</Text>
      <VStack className="gap-1">
        <Icon
          iconName="AtSignIcon"
          text={item.accountId || '-'}
          colorVariant="neutral"
        />
        <Icon
          iconName="LinkIcon"
          text={item.accountUrl || '-'}
          colorVariant="neutral"
        />
      </VStack>
    </div>
  );
}

/**
 * SNSアカウントのリストを入力する CFormItem
 */
export const CFormSnsAccountListItem = buildCFormItem((props: Props) => {
  const tc = useCommonTranslation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.snsAccountItem',
  });
  const idPrefix = useId();
  const values = props.value ?? [];

  const SNS_TYPES: { id: SnsType; label: string }[] = [
    { id: 'Twitter', label: t('snsType.Twitter') },
    { id: 'Instagram', label: t('snsType.Instagram') },
    { id: 'Facebook', label: t('snsType.Facebook') },
    { id: 'Line', label: t('snsType.Line') },
  ];

  const getSnsTypeLabel = (snsType: SnsType): string =>
    SNS_TYPES.find((opt) => opt.id === snsType)?.label ?? snsType;

  const handleAdd = () => {
    props.onChange([
      ...values,
      { snsType: 'Twitter', accountId: '', accountUrl: '' },
    ]);
  };

  const handleRemove = (index: number) => {
    props.onChange(values.filter((_, i) => i !== index));
  };

  const handleChange = (
    index: number,
    field: keyof SnsAccountListItem,
    value: string,
  ) => {
    const next = [...values];
    next[index] = { ...next[index], [field]: value };
    props.onChange(next);
  };

  const renderReadMode = () => {
    if (values.length === 0) {
      return (
        <Text variant="pSmall" className="text-neutral-muted-on-fill">
          -
        </Text>
      );
    }

    return (
      <VStack className="gap-4 w-full">
        {values.map((item, index) => (
          <SnsAccountCard
            key={`${idPrefix}-read-${index}`}
            item={item}
            getSnsTypeLabel={getSnsTypeLabel}
          />
        ))}
      </VStack>
    );
  };

  const renderWriteMode = () => {
    return (
      <VStack className="gap-3 w-full">
        {values.map((item, index) => (
          <VStack
            key={`${idPrefix}-write-${index}`}
            className="gap-2 border-l-2 border-neutral-muted-border pl-4 py-2 w-full"
          >
            <HStack className="justify-between items-center">
              <Text variant="label" className="text-neutral-muted-on-fill">
                {item.snsType ? getSnsTypeLabel(item.snsType) : ''}
              </Text>
              {!props.readonly && (
                <ActionButton
                  icon="Trash2Icon"
                  variant="ghost"
                  iconColor="muted"
                  onClick={() => handleRemove(index)}
                  aria-label={tc('action.delete')}
                />
              )}
            </HStack>

            <SelectField
              value={item.snsType ?? undefined}
              options={SNS_TYPES}
              placeholder="SNS"
              onValueChange={(val) => handleChange(index, 'snsType', val)}
              disabled={props.readonly}
              className="w-[180px]"
            />

            <VStack className="gap-1">
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                {t('accountId')}
              </Text>
              <Input
                value={item.accountId ?? ''}
                onChange={(e) =>
                  handleChange(index, 'accountId', e.target.value)
                }
                disabled={props.readonly}
              />
            </VStack>

            <VStack className="gap-1">
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                {t('url')}
              </Text>
              <Input
                value={item.accountUrl ?? ''}
                onChange={(e) =>
                  handleChange(index, 'accountUrl', e.target.value)
                }
                disabled={props.readonly}
              />
            </VStack>
          </VStack>
        ))}

        {!props.readonly && (
          <ActionButton
            className="pl-0 self-start"
            icon="PlusIcon"
            onClick={handleAdd}
            variant="ghost"
            iconColor="primary"
          >
            {tc('action.add')}
          </ActionButton>
        )}
      </VStack>
    );
  };

  return (
    <CFormItemBase
      {...props}
      readComponent={renderReadMode()}
      writeComponent={renderWriteMode()}
    />
  );
});
